import { setupOidcClient, LogLevel } from 'piral-oidc';
import { WebStorageStateStore } from 'oidc-client';
import { loadConfig } from '../config';

export function createClient(identityProviderUri: string) {
  const config: any = {
    clientId: 'co4_portal',
    identityProviderUri,
    redirectUri: location.origin + '/auth',
    postLogoutRedirectUri: location.origin + '/',
    responseType: 'code',
    logLevel: LogLevel.warn,
    scopes: ['email', 'offline_access', 'openid', 'profile'],
    // Use localStorage so multiple tabs share the same session (avoids re-login per tab)
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };

  // Always set signInRedirectParams to preserve the current URL through OIDC flow
  // If we're on /auth or /logout, redirect to root instead to avoid loops
  const isAuthCallback = location.pathname.startsWith('/auth') || location.pathname.startsWith('/logout');
  const redirectTarget = isAuthCallback ? location.origin + '/' : location.href;

  config.signInRedirectParams = {
    skipUserInfo: true,
    state: {
      finalRedirectUri: redirectTarget,
    },
  };

  const client = setupOidcClient(config);

  // Enable automatic silent token renewal to prevent expired tokens in idle tabs
  const userManager = client._;
  if (userManager) {
    client.logout = () =>
      userManager.signoutRedirect({
        extraQueryParams: { client_id: config.clientId },
      });

    // Start automatic silent renew to refresh tokens before they expire
    // This prevents the issue where idle tabs have expired tokens when the user navigates
    try {
      userManager.startSilentRenew();
    } catch (error) {
      console.warn('Failed to start silent renew:', error);
    }

    userManager.events.addSilentRenewError((error) => {
      console.warn('Silent token renewal failed:', error);
      // Don't redirect to login here - let handleAuthentication() handle it
    });

    // Handle access token expiring - this fires before the token expires
    userManager.events.addAccessTokenExpiring(() => {
      // The automatic silent renew should handle this, but we log it for debugging
      console.debug('Access token expiring, silent renew should trigger automatically');
    });
  }

  return client;
}

const config = await loadConfig();

export const client = createClient(config.OIDC_URL);

/**
 * Clears any stale OIDC user and in-flight signin state from storage. Used to recover from a
 * failed signin callback (e.g. leftover cross-user state in the shared localStorage store after
 * a user switch) before retrying a clean login, instead of force-logging-out (CO4-3273).
 */
export async function clearOidcSession(): Promise<void> {
  const userManager = client._;
  if (!userManager) {
    return;
  }

  try {
    await userManager.removeUser();
    await userManager.clearStaleState();
  } catch (error) {
    console.warn('Failed to clear stale OIDC session state:', error);
  }
}
